<template>
  <dropdown
    ref="dropdownRef"
    title="Assignees"
    class="ifp-filter-assignees"
    @open="handleDropdownOpen()"
  >
    <base-ifp-filter
      label="Assignee"
      clearable
      @clear-clicked="handleClearClick()"
    >
      <template #value-display>
        <!-- N creators selected -->
        <avatar-gallery 
          v-if="hasSelectedAssignees"
          :people="selectedAssignees" 
          :max-num-avatars="3" 
          :size="18" 
          stack-tightly
          suppress-avatar-tooltip
          suppress-remaining-people-tooltip
        />
        <template v-else-if="assignedNotToYou">
          All but you
        </template>
      </template>
    </base-ifp-filter>
    <template #content>
      <div>
        <sleek-filter-input
          v-if="showFilterInput"
          ref="sleekFilterInputRef"
          v-model="filterQuery"
          placeholder="Search by name…"
          show-small-text
        />
        <ul>
          <li
            v-for="assignee in filteredOptions"
            :key="assignee.id"
            @click="toggleSelectedAssignee(assignee.id)"
          >
            <avatar :size="20" :person="assignee" />
            <span
              :class="[
                { 'selected': (assignedNotToYou && isAssigneeNotLoggedInUser(assignee.id)) || isSelected(assignee.id) },
                { 'muted' : assignedNotToYou && isAssigneeNotLoggedInUser(assignee.id) }
              ]">
              {{ assignee.name }}
              <i v-if="assignee.isGuest" class="ddi ddi-warning" title="Guest user" />
            </span>
          </li>
        </ul>
        <hr>
        <ul>
          <li @click="handleAssignedNotToYouClick()">
            <span :class="{ 'selected' : assignedNotToYou }">
              <i class="ddi ddi-people" /><span>All but you</span>
            </span>
          </li>
        </ul>
      </div>
    </template>
  </dropdown>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */
import SearchableDropdownMixin from '@/mixins/searchable-dropdown.mixin'

/* Components */

/* Helpers */
import AssigneeAssistant from '@/helpers/assignee-assistant'

/* Data types */
import { IPersonWithEmail } from '@/api/types/response.types'

/* API */

export default defineComponent({
  name: 'IfpFilterAssignees',

  extends: SearchableDropdownMixin,

  props: {
    assigneeOptions: {
      type: Array as () => IPersonWithEmail[],
      required: true,
    },
    assignedNotToYou: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['assigned-not-to-you-updated'],

  data() {
    return {
      /* SearchableDropdownMixin configuration */
      allowMultipleSelections: true,
    }
  },

  computed: {
    totalOptionsCount(): number {
      return this.curatedAssigneeOptions.length
    },
    curatedAssigneeOptions(): IPersonWithEmail[] {
      return AssigneeAssistant.getFullAssigneeOptions(
        this.assigneeOptions,
        this.$permissions,
      )
    },
    filteredOptions(): IPersonWithEmail[] {
      if (this.filterQuery.length === 0) {
        return this.curatedAssigneeOptions
      }

      return this.curatedAssigneeOptions.filter(person => {
        return person.name
          .toLowerCase()
          .includes(this.filterQuery.toLowerCase())
      })
    },
    hasSelectedAssignees(): boolean {
      if (this.selectedIds.length === 0) {
        return false
      }

      return this.curatedAssigneeOptions.some(p =>
        this.selectedIds.includes(p.id),
      )
    },
    selectedAssignees(): IPersonWithEmail[] {
      return this.curatedAssigneeOptions.filter(p =>
        this.selectedIds.includes(p.id),
      )
    },
  },

  methods: {
    isAssigneeNotLoggedInUser(id: number) {
      return id !== this.$permissions.loggedInUser.id && id !== 0
    },
    toggleSelectedAssignee(id: number) {
      if (this.assignedNotToYou) {
        // If going from "All but you" to a selected assignee,
        // update both the 'assigned_not_to_you' boolean and the 'selectedIDs'
        // immediately to prevent a double call with the normal debounce
        this.handleSelection(id, true)
        this.updatedAssignedNotToYou(false)
      } else {
        this.handleSelection(id)
      }
    },
    updatedAssignedNotToYou(val: boolean) {
      this.$emit('assigned-not-to-you-updated', val)
    },
    handleClearClick() {
      this.clearSelectedIDs()
      this.updatedAssignedNotToYou(false)
    },
    handleAssignedNotToYouClick() {
      this.clearSelectedIDs()
      this.clearFilterQuery()
      this.updatedAssignedNotToYou(!this.assignedNotToYou)
    },
  },
})
</script>
<style lang="scss">
.ifp-filter-assignees {
  .ddi-warning {
    color: $color--orange-40 !important;
  }
}
</style>
